import React, { useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { CircularProgress, ThemeProvider } from '@material-ui/core';

import { Product, getTheme } from '@/utils/productHelpers';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSparkConfirm, requestAuthCode } from '@/utils/sparkActivationApi';
import { REDIRECT_TO_SESSION_KEY } from '@/utils/constants';
import { isSafeRedirectUri } from '@/utils/urlHelpers';
import { ME_QUERY } from '@/apollo/queries';
import { logError } from '@/utils/logError';

const getEntitlementStatus = (accessToken: string): string | undefined => {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    return payload.bh_claims?.products?.[0]?.status;
  } catch {
    return undefined;
  }
};

const SparkEmailCallback: React.FC = () => {
  const history = useHistory();
  const { product } = useParams<{ product: Product }>();
  const { search } = useLocation();
  const [fetchMe] = useLazyQuery<{ me: { uid: string } }>(ME_QUERY, { fetchPolicy: 'no-cache' });

  useEffect(() => {
    const handleLoginFlow = async () => {
      const {
        data: { session: refreshedSession },
        error: refreshError,
      } = await getSupabaseClient().auth.refreshSession();

      if (refreshError || !refreshedSession?.access_token || !refreshedSession?.refresh_token) {
        logError('[SparkEmailCallback]', 'refreshSession failed', refreshError);
        history.replace('/spark/login');
        return;
      }

      const redirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY);

      if (!redirectTo || !isSafeRedirectUri(redirectTo)) {
        history.replace(`/activate/${product}/download-app`);
        return;
      }

      let exchangeToken: string;
      try {
        ({ exchangeToken } = await requestAuthCode(
          refreshedSession.access_token,
          refreshedSession.refresh_token,
        ));
      } catch (err) {
        logError('[SparkEmailCallback]', 'requestAuthCode failed', err);
        history.replace('/spark/login');
        return;
      }

      const { data: meData } = await fetchMe({
        context: { headers: { Authorization: `Bearer ${refreshedSession.access_token}` } },
      });
      const limbixUserUid = meData?.me?.uid;

      const deepLinkUrl = new URL(redirectTo);
      deepLinkUrl.searchParams.set('exchange_token', exchangeToken);
      if (limbixUserUid) {
        deepLinkUrl.searchParams.set('limbix_user_uid', limbixUserUid);
      }

      window.location.href = deepLinkUrl.toString();
    };

    const handleCallback = async () => {
      try {
        const redirectToParam = new URLSearchParams(search).get('redirectTo');
        if (redirectToParam && isSafeRedirectUri(redirectToParam)) {
          sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectToParam);
        }

        await new Promise((resolve) => { setTimeout(resolve, 1500); });

        const { data: { session } } = await getSupabaseClient().auth.getSession();

        if (!session) {
          history.replace(`/activate/${product}/access-code`, { error: 'email_verification_failed' });
          return;
        }

        const supabaseUserId = session.user?.id ?? '';
        const patientUid = session.user?.user_metadata?.limbixUserId || '';
        const entitlementStatus = getEntitlementStatus(session.access_token);
        const rawAccessCode = session.user?.user_metadata?.accessCode;
        const accessCode = typeof rawAccessCode === 'string' && rawAccessCode.trim()
          ? rawAccessCode.trim()
          : null;
        const isDependent = session.user?.user_metadata?.isDependent === true;
        // Whether to skip activateSparkConfirm depends only on actual entitlement status —
        // NOT on isDependent. A dependent's FIRST magic link still needs confirm to run
        // (it's what flips entitlement pending → active); skipping it here left every
        // dependent stuck with a pending entitlement and no way to complete session
        // handoff (SD-2335). Once a dependent's entitlement is active, later magic-link
        // clicks correctly skip confirm same as any other returning user.
        const isReturningUser = !accessCode && entitlementStatus === 'active';

        if (isReturningUser) {
          await handleLoginFlow();
          return;
        }

        const orgSlug = new URLSearchParams(search).get('org_slug');

        try {
          await activateSparkConfirm({ supabaseUserId, accessToken: session.access_token });
        } catch (err) {
          logError('[SparkEmailCallback]', 'activation failed', err, { supabaseUserId });
          history.replace(`/activate/${product}/access-code`, { error: 'email_verification_failed' });
          return;
        }

        // Dependents have their name/DOB and coverage check completed by their caregiver
        // during enrollment, before the dependent ever receives a magic link — so once
        // confirm has activated their entitlement, skip straight to the logged-in handoff
        // flow instead of re-showing coverage-check/user-info screens. (SD-2335)
        if (isDependent) {
          await handleLoginFlow();
          return;
        }

        if (orgSlug && !accessCode) {
          history.replace(`/activate/${product}/coverage-check/${orgSlug}`, {
            patientUid,
            flow: 'email',
            accessToken: session.access_token,
          });
        } else {
          history.replace(`/activate/${product}/user-info/confirmation`, {
            patientUid,
            flow: 'email',
            accessCode,
          });
        }
      } catch (err) {
        logError('[SparkEmailCallback]', 'Unexpected error', err);
        history.replace(`/activate/${product}/access-code`, { error: 'email_verification_failed' });
      }
    };

    handleCallback();
  }, [history, product, search, fetchMe]);

  return (
    <ThemeProvider theme={getTheme(product)}>
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
      }}
      >
        <CircularProgress color="primary" />
      </div>
    </ThemeProvider>
  );
};

export default SparkEmailCallback;
